/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef } from 'react'
import './MessageCenterContactStyle.css'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import useContact from './useContact'
import { useMessageCenterTabLayer } from '../MessageCenterTabLayer.context'
import { getFullName } from '@utils/fullname.util'
import Button from '@components/common/Button/Button.component'
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons'
import { ColumnModel } from '@syncfusion/ej2-react-grids'
import { IContact } from '@dataTypes/contact.type'
import { getFullAddress } from '@utils/address.util'
import {
  AddContactModal,
  ConfirmBlockModal,
  SelectContactModal,
} from '@components/layout/Modals'
import { formatPhoneNumber } from '@utils/numberFormatter.util'
import { ClipMode, TextAlign } from '@constants/Common/GridComponent.contants'
import { SearchWithFilter } from '@components/common'
import { EmptyState } from '@components/ui'
import { useNoteEditorOverlay } from '@components/common/NoteEditorOverlay'
import type { Patient } from '@dataTypes/PatientInfo.types'
import { fetchPatientById } from '@services/api/patient.api'
import { usePageContentGridScrollMaxHeight } from '@hooks/usePageContentGridScrollMaxHeight'

const MessageCenterContact = (): React.ReactElement => {
  const { setTabLayerActions } = useMessageCenterTabLayer()

  const { openNoteEditor, updateNoteEditor, closeNoteEditor } =
    useNoteEditorOverlay()
  const { ...hooks } = useContact({
    onOpenNotesOverlay: (state, patient): void => {
      openNoteEditor({
        patient: patient ?? null,
        contextId: state.contextId ?? null,
        contextType: state.contextType ?? null,
        contextName: state.contextName ?? null,
        contactId: state.contactId ?? null,
        openFirstNote: state.openFirstNote ?? false,
        openNoteId: state.openNoteId ?? null,
        pagePreviousState: state.previousPage ?? null,
        initialView: state.openFirstNote ? 'list' : 'add',
        overlayBackLabel: 'Back to contacts',
        editorBackButtonLabel: 'Back to contacts',
        openContextNotesByDefault: true,
        hideNotesTable: true,
        notesFetchLoadingContainer: 'body',
        portalTarget: '.page-content',
      })
      if (patient?.patient_id) return
      if (state.patientId) {
        void fetchPatientById(state.patientId)
          .then((response) => {
            updateNoteEditor({
              patient: response.data.patient as unknown as Patient,
            })
          })
          .catch(() => {
            closeNoteEditor()
          })
      }
    },
  })

  const hasNoContacts = hooks.contactsState.length === 0
  const showEmptyState =
    !hooks.contactsLoading && hooks.filteredContacts.length === 0
  const disableHeaderActions = showEmptyState && hasNoContacts

  useEffect(() => {
    setTabLayerActions(
      <div className="mc-contact-buttons">
        <SearchWithFilter
          searchIcon="mgc_search_2_line"
          filterIcon="mgc_settings_2_line"
          placeholder="Search"
          searchQuery={hooks.searchQuery}
          onChange={hooks.handleSearch}
          className="mcc-filter"
          filters={hooks.filterSelectionList}
          onFilterChange={hooks.handleFilterChange}
          filterLabels={{
            city: 'City',
            state: 'State',
          }}
        />
        <Button
          size="md"
          className="button-secondary add-contact-button"
          onClick={hooks.handleShowManageGroupModal}
          disabled={disableHeaderActions}
        >
          Manage groups
        </Button>
        <Button
          size="md"
          className="button-primary add-contact-button"
          onClick={hooks.handleAddContact}
          disabled={disableHeaderActions}
        >
          Add contacts
        </Button>
      </div>,
    )
    return () => setTabLayerActions(null)
  }, [
    disableHeaderActions,
    hooks.filterSelectionList,
    hooks.filters,
    hooks.searchQuery,
    hooks.handleAddContact,
    hooks.handleShowManageGroupModal,
    setTabLayerActions,
  ])

  const columns: ColumnModel[] = useMemo(
    () => [
      {
        headerText: 'Contact name',
        field: 'first_name',
        template: (props: IContact) => (
          <div tabIndex={0} className="name-col">
            <span
              className={`name${
                hooks.blockedUsers.find(
                  (blockedUser) => blockedUser.contact_id === props.contact_id,
                )
                  ? ' danger'
                  : ''
              }`}
            >
              {getFullName(props, true)}
            </span>
          </div>
        ),
        width: 0.21,
      },
      {
        headerText: 'Contact number',
        field: '_cellphone',
        width: 0.21,
      },
      {
        headerText: 'Email address',
        field: 'email',
        width: 0.21,
        clipMode: ClipMode.EllipsisWithTooltip,
      },
      {
        headerText: 'Home address',
        field: '_address',
        width: 0.21,
        clipMode: ClipMode.EllipsisWithTooltip,
      },
      {
        headerText: 'Notes',
        field: 'notes',
        template: (props: IContact) => {
          return (
            <div className="action-col">
              {props.contact_note?.length ? (
                <Button
                  className="button-secondary view-note"
                  type="button"
                  title="View note"
                  aria-label="View note"
                  onClick={() => {
                    hooks.onShowNotes(props)
                  }}
                >
                  <span className="mgc_document_fill" />
                </Button>
              ) : (
                <span className="mb-0">-</span>
              )}
            </div>
          )
        },
        width: 0.06,
        textAlign: TextAlign.Center,
      },
      {
        headerText: 'Actions',
        field: '',
        template: (props: IContact) => {
          const items = [
            ...(hooks.blockedUsers.find(
              (blockedUser) => blockedUser.contact_id === props.contact_id,
            )
              ? [
                  {
                    id: 'unblock',
                    text: 'Unblock contact',
                    iconCss: 'mgc_user_follow_fill',
                  },
                ]
              : []),
            {
              id: 'edit',
              text: 'Edit contact',
              iconCss: 'mgc_pencil_fill',
            },
            {
              id: 'set-appointment',
              text: 'Set appointment',
              iconCss: 'mgc_bell_ringing_fill',
            },
            {
              id: 'add-note',
              text: 'Add note',
              iconCss: 'mgc_document_fill',
            },
            {
              id: 'delete',
              text: 'Delete contact',
              iconCss: 'mgc_delete_2_fill',
            },
          ]

          return (
            <div className="action-buttons">
              <Button
                className="action-button-filled"
                onClick={() => {
                  hooks.handlePhoneAction(props)
                }}
              >
                <i className="mgc_phone_fill" />
              </Button>
              <Button className="action-button" disabled>
                <i className="ti ti-mail-filled" />
              </Button>
              <Button
                className="action-button"
                onClick={() => {
                  hooks.handleSendMessage(props)
                }}
              >
                <i className="ti ti-message-filled" />
              </Button>
              <DropDownButtonComponent
                items={items}
                cssClass="acm-dd custom-dropdown-icon e-caret-hide action-button"
                iconCss="ti ti-dots-vertical"
                beforeItemRender={(args) => {
                  if (args.item.text === 'Delete') {
                    args.element.classList.add('text-default')
                  }
                }}
                onClick={() => {
                  hooks.onClickDropdownAction(props)
                }}
                select={(args) => {
                  hooks.handleDropdownAction(args, props)
                }}
                style={{ height: '24px', width: '24px' }}
              />
            </div>
          )
        },
        width: 0.11,
        textAlign: TextAlign.Center,
        headerTextAlign: TextAlign.Center,
      },
    ],
    [hooks.filteredContacts, hooks.blockedUsers],
  )

  const contactGridSectionRef = useRef<HTMLDivElement>(null)

  const contactGridScrollStyle = usePageContentGridScrollMaxHeight(
    contactGridSectionRef,
    [hooks.filteredContacts.length, hooks.contactsState.length],
    { disabled: showEmptyState },
  )

  return (
    <div className={`mc-contact${showEmptyState ? ' mc-contact--empty' : ''}`}>
      {showEmptyState ? (
        <EmptyState
          className="mc-contact-empty"
          iconClass="mgc mgc_contacts_3_fill"
          title="No contacts found"
          description={
            hasNoContacts
              ? "You don't have any contacts on record yet."
              : 'No contacts match your search.'
          }
          actionLabel={hasNoContacts ? 'Add contacts' : undefined}
          onAction={hasNoContacts ? hooks.handleAddContact : undefined}
        />
      ) : (
        <div
          ref={contactGridSectionRef}
          className="message-grid-scroll-section"
          style={contactGridScrollStyle}
        >
          <DataGridComponent
            allowPaging={true}
            allowSorting={true}
            sortSettings={{
              columns: [{ field: 'first_name', direction: 'Ascending' }],
            }}
            data={hooks.filteredContacts.map((contact) => ({
              ...contact,
              patient_provider_id:
                contact.patient?.public_id ??
                contact.provider?.public_id ??
                'N/A',
              _cellphone: formatPhoneNumber(contact.cellphone),
              _address: getFullAddress(contact),
            }))}
            columns={columns}
          />
        </div>
      )}

      {hooks.showAddContactModal && (
        <AddContactModal
          show={hooks.showAddContactModal}
          onHide={hooks.handleCloseAddContactModal}
          initialContactData={hooks.selectedContact ?? undefined}
        />
      )}
      {hooks.showSelectContactModal && (
        <SelectContactModal
          show={hooks.showSelectContactModal}
          onHide={hooks.handleCloseSelectContactModal}
          contactData={hooks.selectedContact as IContact}
        />
      )}
      {hooks.showConfirmUnblockModal && (
        <ConfirmBlockModal
          show={hooks.showConfirmUnblockModal}
          onHide={hooks.onHideConfirmBlockModal}
          mainText={`Unblock ${getFullName(hooks.selectedContact, true)}?`}
          subText="After unblocking, they will be able to message you again."
          action="Unblock"
          onConfirm={hooks.onUnblockUser}
        />
      )}
    </div>
  )
}

export default MessageCenterContact
